import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bell, LogOut, Plus, Users } from "lucide-react";
import { usePrivatePage } from "../lib/seo";
import { Header } from "../components/Header";
import { CoachAuth } from "../components/coach/CoachAuth";
import { CoachIntro } from "../components/coach/CoachIntro";
import { FirstSteps } from "../components/coach/FirstSteps";
import { FundraiserInfo } from "../components/coach/FundraiserInfo";
import { PayoutForm } from "../components/coach/PayoutForm";
import { DeliveryForm } from "../components/coach/DeliveryForm";
import { FundraiserControl } from "../components/coach/FundraiserControl";
import { FundsSummary, raisedText } from "../components/coach/FundsSummary";
import { SpotTheBall } from "../components/coach/SpotTheBall";
import { api, errMsg, getCoachToken, setCoachToken, rememberTeam } from "../lib/api";

const Notifications = ({ data, onRead }) => (
  <section className="border-2 border-slate-900 bg-white p-5 shadow-[6px_6px_0_0_#0f172a]" data-testid="coach-notifications">
    <div className="mb-3 flex items-center justify-between">
      <h2 className="flex items-center gap-2 font-display text-lg font-extrabold uppercase"><Bell className="h-5 w-5" /> Updates
        {data.unread > 0 && <span className="bg-lime-300 px-2 text-sm" data-testid="coach-unread-count">{data.unread} new</span>}</h2>
      {data.unread > 0 && <button type="button" onClick={onRead} className="text-xs font-bold uppercase text-slate-500 hover:text-slate-900" data-testid="coach-mark-read-button">Mark all read</button>}
    </div>
    {data.items.length === 0 && <p className="text-sm text-slate-500" data-testid="coach-no-notifications">You'll be told here when a parent finishes their child's photo and statistics.</p>}
    <ul className="divide-y">
      {data.items.map((n) => (
        <li key={n.id} className={`flex items-start justify-between gap-3 py-2.5 text-sm ${n.read ? "text-slate-500" : "font-semibold"}`} data-testid={`coach-notification-${n.draft_id}`}>
          <span>{!n.read && <span className="mr-2 inline-block h-2 w-2 rounded-full bg-lime-500" />}{n.message} <span className="text-xs text-slate-400">· {n.team_name} · {new Date(n.created_at).toLocaleString("en-IE")}</span></span>
          <Link to={`/d/${n.draft_id}`} className="shrink-0 text-xs font-bold uppercase underline">Review</Link>
        </li>
      ))}
    </ul>
  </section>
);

export default function Coach() {
  usePrivatePage("Coach dashboard");
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [user, setUser] = useState(getCoachToken() ? null : false);
  const [teams, setTeams] = useState([]);
  const [notes, setNotes] = useState({ unread: 0, items: [] });
  const [name, setName] = useState("");
  const [funds, setFunds] = useState(null);

  const load = () => {
    api.get("/coach/teams").then((r) => setTeams(r.data));
    api.get("/coach/funds").then((r) => setFunds(r.data));
    api.get("/coach/notifications").then((r) => { setNotes(r.data); window.dispatchEvent(new Event("ss-coach-updated")); });
  };
  useEffect(() => {
    if (user !== null) return;
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { setCoachToken(null); setUser(false); });
  }, [user]);
  useEffect(() => { if (user) load(); }, [user]);

  const signedIn = (u) => { const next = params.get("next"); if (next) nav(next); else setUser(u); };
  const create = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/teams", { name: name.trim() || "My squad" }); rememberTeam(data); nav(`/team/${data.id}`); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="min-h-screen pb-10">
      <Header onNew={() => nav("/design")} />
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        {user === false && (
          <div className="grid gap-10 lg:grid-cols-[1fr_380px] lg:items-start">
            <div className="order-2 min-w-0 lg:order-1"><CoachIntro /></div>
            <div className="order-1 lg:sticky lg:top-24 lg:order-2"><CoachAuth onDone={signedIn} initialMode={params.get("mode") === "register" ? "register" : "login"} /></div>
          </div>
        )}
        {user && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div><div className="eyebrow">Coach dashboard</div><h1 className="font-display text-4xl font-black uppercase leading-none" data-testid="coach-name">Hi, {user.name}</h1></div>
              <button type="button" onClick={() => { setCoachToken(null); setUser(false); window.dispatchEvent(new Event("ss-coach-updated")); }} className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="coach-logout-button"><LogOut className="h-4 w-4" /> Sign out</button>
            </div>
            <a href="/help#coach" className="flex items-center justify-between border-2 border-lime-400 bg-lime-50 p-4 text-sm font-bold hover:bg-lime-100" data-testid="coach-help-link">New here? Watch the 2-minute coach video guide <span aria-hidden>→</span></a>
            <FundsSummary funds={funds} />
            <FundraiserControl funds={funds} onChanged={load} />
            <FirstSteps teams={teams} />
            <Notifications data={notes} onRead={() => api.post("/coach/notifications/read").then(load)} />
            <section className="space-y-3" data-testid="coach-teams">
              <h2 className="font-display text-lg font-extrabold uppercase">My squads</h2>
              {teams.map((t) => (
                <Link key={t.id} to={`/team/${t.id}`} className="card-lift flex items-center justify-between border-2 border-slate-200 bg-white p-4" data-testid={`coach-team-${t.id}`}>
                  <span className="flex items-center gap-2 font-bold"><Users className="h-4 w-4" /> {t.name}</span><span className="text-right text-sm text-slate-500">{t.players} player{t.players === 1 ? "" : "s"}<span className="block text-xs font-bold text-lime-700" data-testid={`coach-team-raised-${t.id}`}>{raisedText(funds?.teams?.[t.id])}</span></span>
                </Link>
              ))}
              <form onSubmit={create} className="flex gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="New squad name, e.g. U12 Girls" data-testid="coach-new-team-input"
                  className="min-w-0 flex-1 border-2 border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-slate-900" />
                <button type="submit" className="btn-blaze flex items-center gap-1.5 px-4 py-2 text-sm" data-testid="coach-new-team-button"><Plus className="h-4 w-4" /> New squad</button>
              </form>
            </section>
            <SpotTheBall />
            <PayoutForm />
            <DeliveryForm />
            <details className="border-2 border-slate-200 bg-white p-5" data-testid="coach-fundraiser-details">
              <summary className="cursor-pointer font-display text-lg font-extrabold uppercase">How the fundraiser works</summary>
              <div className="mt-4"><FundraiserInfo /></div>
            </details>
          </div>
        )}
      </main>
    </div>
  );
}
